// src/button/button.tsx
import { cx } from "class-variance-authority";

// src/button/button.styles.ts
import { cva } from "class-variance-authority";
var buttonVariants = cva(
  [
    "flex items-center justify-center rounded-md p-1",
    "min-h-[40px] w-full",
    "text-xs font-inter font-semibold",
    "transition-colors duration-150 ease-in-out",
    "group"
  ],
  {
    variants: {
      variant: {
        primary: "",
        secondary: "",
        toggle: ""
      },
      pressed: { true: "", false: "" },
      disabled: { true: "", false: "" }
    },
    compoundVariants: [
      {
        variant: "toggle",
        pressed: true,
        disabled: false,
        class: "bg-accent-secondary hover:bg-accent-secondary/80"
      },
      {
        variant: "toggle",
        pressed: false,
        disabled: false,
        class: "bg-dark-grey-4 hover:bg-dark-grey-3 text-accent-secondary"
      },
      {
        disabled: true,
        class: "border border-solid border-dark-grey-3 bg-dark-grey-5 cursor-not-allowed text-dark-grey-1"
      }
    ],
    defaultVariants: {
      variant: "toggle",
      pressed: false,
      disabled: false
    }
  }
);

// src/button/button.tsx
import { jsx } from "react/jsx-runtime";
function Button({
  pressed = false,
  disabled,
  variant,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      disabled,
      "data-pressed": pressed ? "true" : "false",
      className: cx(buttonVariants({ variant, pressed, disabled }), className),
      ...rest
    }
  );
}

// src/card/base/card-base.tsx
import { forwardRef as forwardRef2 } from "react";

// src/card/base/card-base.styles.ts
import { cva as cva2 } from "class-variance-authority";
var cardBaseVariants = cva2(
  "flex flex-col gap-4 items-start justify-start leading-[0] not-italic relative size-full bg-dark-grey-5 p-6 rounded-lg"
);
var cardBaseHeaderVariants = cva2(
  'flex flex-col font-["Inter:Bold",_sans-serif] font-bold justify-end min-w-full relative shrink-0 text-xl leading-5 text-white'
);
var cardBaseContentVariants = cva2(
  'font-["Inter:Regular",_sans-serif] font-normal min-w-full relative shrink-0 text-sm leading-5 text-dark-grey-1'
);
var cardBaseFooterVariants = cva2(
  "flex items-center justify-between min-w-full relative shrink-0"
);

// src/card/base/card-base.sub-components.tsx
import { forwardRef } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Header = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "div",
      {
        ref,
        className: cardBaseHeaderVariants({ className }),
        ...props,
        children
      }
    );
  }
);
Header.displayName = "CardBase.Header";
var Content = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "div",
      {
        ref,
        className: cardBaseContentVariants({ className }),
        ...props,
        children
      }
    );
  }
);
Content.displayName = "CardBase.Content";
var Footer = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "div",
      {
        ref,
        className: cardBaseFooterVariants({ className }),
        ...props,
        children
      }
    );
  }
);
Footer.displayName = "CardBase.Footer";

// src/card/base/card-base.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var CardBase = forwardRef2(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx3("div", { ref, className: cardBaseVariants({ className }), ...props, children });
  }
);
CardBase.displayName = "CardBase";
CardBase.Header = Header;
CardBase.Content = Content;
CardBase.Footer = Footer;

// src/embla-carousel/embla-carousel.tsx
import useEmblaCarousel from "embla-carousel-react";
import { forwardRef as forwardRef4 } from "react";

// src/embla-carousel/embla-carousel.styles.ts
import { cva as cva3 } from "class-variance-authority";
var emblaCarouselVariants = cva3("overflow-hidden p-0.5 select-none");
var emblaViewportVariants = cva3("cursor-default");
var emblaContainerVariants = cva3("flex");
var emblaSlideVariants = cva3("flex-grow-0 flex-shrink-0 min-w-0");

// src/embla-carousel/embla-carousel.sub-components.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Viewport = forwardRef3(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx4(
      "div",
      {
        ref,
        className: emblaViewportVariants({ className }),
        ...props,
        children
      }
    );
  }
);
Viewport.displayName = "EmblaCarousel.Viewport";
var Container = forwardRef3(({ children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx4("div", { ref, className: emblaContainerVariants({ className }), ...props, children });
});
Container.displayName = "EmblaCarousel.Container";
var Slide = forwardRef3(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx4("div", { ref, className: emblaSlideVariants({ className }), ...props, children });
  }
);
Slide.displayName = "EmblaCarousel.Slide";

// src/embla-carousel/embla-carousel.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var EmblaCarousel = forwardRef4(({ children, className, options, ...props }, ref) => {
  const [emblaRef] = useEmblaCarousel(options);
  return /* @__PURE__ */ jsx5("div", { ref, className: emblaCarouselVariants({ className }), ...props, children: /* @__PURE__ */ jsx5(Viewport, { ref: emblaRef, className: "embla__viewport", children: /* @__PURE__ */ jsx5(Container, { className: "embla__container", children }) }) });
});
EmblaCarousel.displayName = "EmblaCarousel";
EmblaCarousel.Slide = Slide;

// src/input/index.tsx
import clsx from "clsx";
import React from "react";
import { jsx as jsx6, jsxs } from "react/jsx-runtime";
var Input = React.forwardRef(
  ({ label, error, className, ...rest }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: clsx("flex flex-col text-white", className), children: [
      label && /* @__PURE__ */ jsx6("label", { htmlFor: rest.id, className: "mb-1 text-sm font-medium", children: label }),
      /* @__PURE__ */ jsx6(
        "input",
        {
          ref,
          className: clsx(
            "border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1",
            error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          ),
          ...rest
        }
      ),
      error && /* @__PURE__ */ jsx6("span", { className: "mt-1 text-sm text-red-600", children: error })
    ] });
  }
);
Input.displayName = "Input";

// src/skeleton/skeleton.tsx
import { clsx as clsx2 } from "clsx";
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var Skeleton = forwardRef5(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      ref,
      className: clsx2("animate-pulse rounded-md bg-dark-grey-4", className),
      ...props
    }
  );
});
Skeleton.displayName = "Skeleton";
export {
  Button,
  CardBase,
  EmblaCarousel,
  Input,
  Skeleton,
  cardBaseContentVariants,
  cardBaseFooterVariants,
  cardBaseHeaderVariants,
  cardBaseVariants,
  emblaCarouselVariants,
  emblaContainerVariants,
  emblaSlideVariants,
  emblaViewportVariants
};
